import axios, { AxiosInstance, AxiosRequestConfig, AxiosError } from "axios";
import { applyDevConnectedChannelsOverride } from "@/lib/dev/simulateAllChannelsConnected.js";

/** GET /api/profiles/:profileId/channels (list only — not single-channel by id). */
const PROFILE_CHANNELS_LIST_RE = /^\/api\/profiles\/([^/]+)\/channels\/?$/;

// Get base URL from env, fallback to localhost
export const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:3001";

// CSRF token cache — refreshed from response headers on every request
let csrfToken: string | null = null;

// Create axios instance (raw HTTP — use for routes not wrapped on `api`)
export const client: AxiosInstance = axios.create({
  baseURL: API_BASE,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true, // Send cookies (CSRF double-submit cookie)
});

// Add JWT token + CSRF token to all requests
client.interceptors.request.use(
  (config: any) => {
    const token = localStorage.getItem("jwt_token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Attach CSRF token to state-changing requests
    if (csrfToken && ["post", "put", "patch", "delete"].includes(config.method || "")) {
      config.headers["X-CSRF-Token"] = csrfToken;
    }
    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// Capture CSRF token from response headers + handle 401 errors
client.interceptors.response.use(
  (response) => {
    // Capture CSRF token from any response that includes it
    const newCsrf = response.headers["x-csrf-token"];
    if (newCsrf) {
      csrfToken = newCsrf;
    }
    const url = response.config?.url || "";
    const channelsListMatch = url.match(PROFILE_CHANNELS_LIST_RE);
    if (channelsListMatch && response.data && Array.isArray(response.data.channels)) {
      const profileId = channelsListMatch[1];
      response.data.channels = applyDevConnectedChannelsOverride(
        response.data.channels,
        profileId,
      );
    }
    return response;
  },
  (error: AxiosError) => {
    const url = error.config?.url || "";
    if (
      error.response?.status === 401 &&
      error.config?.baseURL === API_BASE &&
      !url.includes("/auth/login") &&
      !url.includes("/auth/signup")
    ) {
      localStorage.removeItem("jwt_token");
      localStorage.removeItem("user");
      window.dispatchEvent(new Event("auth-logout"));
    }
    if (error.response?.status === 402) {
      const data = error.response.data as { error?: string; data_deletion_date?: string };
      if (data?.error === "TRIAL_EXPIRED") {
        window.dispatchEvent(
          new CustomEvent("trial-expired", {
            detail: { deletionDate: data.data_deletion_date },
          }),
        );
      } else {
        window.dispatchEvent(new CustomEvent("credits:insufficient", { detail: error.response.data }));
      }
    }
    return Promise.reject(error);
  }
);

// ---------------------------------------------------------------------------
// Auth headers helper — used by native fetch (SSE streaming)
// ---------------------------------------------------------------------------

export function getAuthHeaders(): Record<string, string> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const token = localStorage.getItem('jwt_token');
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (csrfToken) headers['X-CSRF-Token'] = csrfToken;
  return headers;
}

/** Populate CSRF cache (axios) before fetch + getAuthHeaders() POSTs. */
export async function primeCsrfToken(): Promise<void> {
  await client.get('/api/csrf-token');
}

// ---------------------------------------------------------------------------
// TypeScript Interfaces — Week 19 Knowledge Center
// ---------------------------------------------------------------------------

export interface Profile {
  id: string;
  company_id: string;
  name: string;
  slug: string;
  client_name: string;
  industry: string;
  website?: string;
  logo_url?: string;
  client_logo_url?: string;
  profile_color?: string;
  profile_icon?: string;
  default_currency?: string | null;
  reporting_currency?: string | null;
  reporting_currency_code?: string;
  display_currency_code?: string;
  timezone?: string | null;
  week_starts_on?: string | null;
  account_currency_code?: string;
  /** Native provider currency known (read-only). Does not lock preferences. */
  currency_locked?: boolean;
  currency_source?: string;
  native_currency_status?: 'single' | 'mixed' | 'unknown';
  distinct_native_currency_codes?: string[];
  reporting_currency_status?: 'explicit' | 'unset' | 'unconfirmed_legacy';
  default_currency_status?: 'explicit' | 'unset' | 'unconfirmed_legacy';
  default_currency_explicit?: boolean | null;
  reporting_currency_explicit?: boolean | null;
  prefs_editable?: boolean;
  is_active: boolean;
  status?: string;
  metadata?: Record<string, any>;
  created_by?: string;
  owner_user_id?: string | null;
  created_at: string;
  updated_at: string;
}

export interface Channel {
  id: string;
  profile_id: string;
  platform: "google_ads" | "meta_ads" | "linkedin_ads";
  connected_account_id?: string;
  is_active: boolean;
  channel_goals?: Record<string, any>;
  channel_notes?: string;
  naming_convention_notes?: string | null;
  created_at: string;
  updated_at: string;
}

export interface ChannelAssignment {
  id: string;
  channel_id: string;
  user_id: string;
  role: "channel_manager" | "viewer";
  assigned_by?: string;
  created_at: string;
}

export interface PerformanceManager {
  id: string;
  profile_id: string;
  user_id: string;
  can_add_business_context: boolean;
  can_manage_budget: boolean;
  can_allocate_skills: boolean;
  can_assign_channels: boolean;
  can_delete_data: boolean;
  created_at: string;
}

export interface KnowledgeItem {
  id: string;
  company_id: string;
  profile_id: string;
  channel_id?: string;
  title: string;
  content?: string;
  description?: string;
  source?: string;

  // Type system (Week 19)
  knowledge_type: "document" | "business_context" | "budget" | "note";

  // Trust level (original system — verified/observed/suggested)
  trust_level?: "verified" | "observed" | "suggested";

  category?: string;
  manager_only: boolean;

  // AI-generated fields
  ai_summary?: string;
  ai_summary_short?: string;
  ai_insights?: {
    key_points?: string[];
    dates?: string[];
    metrics?: string[];
    entities?: string[];
  };

  // Columns the API already returns (select *) — typed for KC v2
  channel_tag?: string;
  document_type?: string;
  source_type?: string;
  tags?: string[];
  is_shared?: boolean;
  visible_to_llm?: boolean;
  quality_score?: number;
  expires_at?: string | null;
  uploaded_by_user_id?: string;
  metadata?: Record<string, any>;

  created_by?: string;
  created_at: string;
  updated_at: string;
  comment_count?: number;
  view_count?: number;
}

export interface Comment {
  id: string;
  knowledge_id: string;
  user_id: string;
  company_id: string;
  comment_text: string;
  parent_comment_id?: string;
  mentions?: string[];
  reactions?: Record<string, string>;
  is_resolved: boolean;
  resolved_by?: string;
  resolved_at?: string;
  created_at: string;
  updated_at: string;
  user?: { id: string; email: string };
  replies?: Comment[];
}

export interface UserPermissions {
  profile_id: string;
  user_id: string;
  is_admin: boolean;
  is_performance_manager: boolean;
  can_add_business_context: boolean;
  can_manage_budget: boolean;
  accessible_channels: string[];
}

// Request types

export interface CreateProfileRequest {
  name: string;
  client_name?: string;
  industry: string;
  website?: string;
  default_currency?: string;
  timezone?: string;
  profile_color?: string;
  profile_icon?: string;
  status?: string;
}

export interface CreateChannelRequest {
  platform: "google_ads" | "meta_ads" | "linkedin_ads";
  connected_account_id?: string;
  channel_goals?: Record<string, any>;
  channel_notes?: string;
  naming_convention_notes?: string | null;
}

export interface CreateKnowledgeRequest {
  title: string;
  content?: string;
  knowledge_type: "document" | "business_context" | "budget" | "note";
  trust_level?: "verified" | "observed" | "suggested";
  category?: string;
  channel_id?: string;
  channel_tag?: string;
  intelligence_document_type?: string;
  document_type?: string;
  tags?: string;
  description?: string;
  source?: string;
  doc_scope?: string;
}

export interface CreateCommentRequest {
  comment_text: string;
  parent_comment_id?: string;
  mentions?: string[];
}

export interface KnowledgeListParams {
  channel_id?: string;
  knowledge_type?: string;
  trust_level?: string;
  category?: string;
  search?: string;
  limit?: number;
  offset?: number;
}

export interface AssignChannelRequest {
  user_id: string;
  channel_id: string;
  role: "channel_manager" | "viewer";
}

export interface AssignPMRequest {
  user_id: string;
  can_add_business_context?: boolean;
  can_manage_budget?: boolean;
  can_allocate_skills?: boolean;
  can_assign_channels?: boolean;
  can_delete_data?: boolean;
}

// ---------------------------------------------------------------------------
// API Endpoints
// ---------------------------------------------------------------------------

export const api = {
  /** Escape hatch for paths not yet wrapped as named methods (existing runtime use). */
  get: <T = any>(url: string, config?: AxiosRequestConfig) => client.get<T>(url, config),
  post: <T = any>(url: string, data?: any, config?: AxiosRequestConfig) =>
    client.post<T>(url, data, config),

  auth: {
    login: (email: string, password: string) =>
      client.post("/auth/login", { email, password }),

    getUser: () => client.get("/auth/me"),

    updateProfile: (body: { full_name?: string; timezone?: string }) =>
      client.patch("/auth/profile", body),

    assignRole: (body: { role: string }) =>
      client.post("/auth/assign-role", body),

    acceptInvitation: (body: {
      token: string;
      firstName: string;
      lastName: string;
      password: string;
    }) =>
      client.post<{
        token: string;
        user: {
          id: string;
          email: string;
          full_name: string;
          company_id: string | null;
          role: string;
          email_verified: boolean;
          trial_ends_at: string | null;
          company_name?: string | null;
        };
      }>("/auth/accept-invitation", body),

    verifyEmail: (token: string) =>
      client.post<{
        success: boolean;
        message: string;
        email: string;
        user?: { id: string; full_name: string | null };
      }>("/auth/verify-email", { token }),

    resendVerification: (data: { email: string }) =>
      client.post<{ message: string }>("/auth/resend-verification", data),

    signup: (data: {
      email: string;
      password: string;
      full_name: string;
      company_name?: string;
      plan: "individual" | "agency" | "trial";
    }) => client.post("/auth/signup", data),
  },

  // ── Profiles (5 endpoints) ──────────────────────────────────────────────

  profiles: {
    list: () =>
      client.get<{ profiles: Profile[] }>("/api/profiles"),

    get: (id: string) =>
      client.get<{ profile: Profile }>(`/api/profiles/${id}`),

    getConnections: (profileId: string) =>
      client.get<{
        profileId: string;
        connections: Array<{
          platform: string;
          connected: boolean;
          account: Record<string, unknown> | null;
          canAdd: boolean;
        }>;
        allConnected: Record<string, unknown>[];
      }>(`/api/profiles/${profileId}/connections`),

    /** Task 72a — reasoning trace + decision memory for “Why this recommendation?” */
    recommendationContext: (profileId: string, messageId: string) =>
      client.get<{
        reasoningTrace: Record<string, unknown> | null;
        decision: Record<string, unknown> | null;
        confidenceComponents: Record<string, number> | null;
        similarCaseSummary: string | null;
        contextSourceLabels: string[];
      }>(`/api/profiles/${profileId}/recommendation-context/${messageId}`),

    /** KB6 — always-on context pins (interaction_memory → explicit_instructions P0) */
    getAlwaysOnContext: (profileId: string) =>
      client.get<{
        maxSlots: number;
        items: Array<{
          id: string;
          kind: 'document' | 'instruction';
          label: string;
          instruction_text?: string;
          context_type?: string;
          slot_index: number;
          scope?: 'PROFILE_SHARED' | 'CHANNEL_SHARED';
          channel?: string;
          scope_explicit?: boolean;
          availability?: 'current' | 'source_unavailable' | 'superseded' | 'not_in_scope';
        }>;
      }>(`/api/profiles/${profileId}/always-on-context`),

    setAlwaysOnContext: (
      profileId: string,
      items: Array<{
        kind: 'document' | 'instruction';
        id?: string;
        label?: string;
        instruction_text?: string;
        context_type?: string;
        knowledge_item_id?: string;
        scope?: 'PROFILE_SHARED' | 'CHANNEL_SHARED';
        channel?: string;
      }>,
    ) =>
      client.put<{
        maxSlots: number;
        items: Array<{
          id: string;
          kind: 'document' | 'instruction';
          label: string;
          instruction_text?: string;
          context_type?: string;
          slot_index: number;
          scope?: 'PROFILE_SHARED' | 'CHANNEL_SHARED';
          channel?: string;
          availability?: 'current' | 'source_unavailable' | 'superseded' | 'not_in_scope';
        }>;
      }>(`/api/profiles/${profileId}/always-on-context`, { items }),

    getPulsePreference: (profileId: string) =>
      client.get<{ preference_text: string; updated_at: string | null }>(
        `/api/profiles/${profileId}/pulse-preference`,
      ),

    setPulsePreference: (profileId: string, preference_text: string) =>
      client.post<{ preference_text: string; updated_at: string | null }>(
        `/api/profiles/${profileId}/pulse-preference`,
        { preference_text },
      ),

    /** Phase 2.5-W2 — KC Chat Memory tab (user_stated_fact rows) */
    getChatMemory: (profileId: string) =>
      client.get<{
        items: Array<{
          id: string;
          profile_id: string;
          canonical_text: string;
          original_text: string;
          memory_class: string;
          status: string;
          speaker_name: string;
          speaker_user_id: string | null;
          created_at: string | null;
          last_reinforced_at: string | null;
          reinforcement_count: number;
          disputed_peer_id: string | null;
          disputed_peer_text: string | null;
          temporal_type: string;
          valid_until: string | null;
          needs: string | null;
          scope_kind?: 'CLIENT_SHARED' | 'CHANNEL_SHARED' | 'USER_WITHIN_PROFILE';
          scope_label?: string;
        }>;
      }>(`/api/profiles/${profileId}/chat-memory`),

    patchChatMemory: (
      profileId: string,
      id: string,
      body: {
        canonical_text?: string;
        status?: string;
        temporal_type?: string;
        valid_until?: string | null;
        confirm_validity?: 'until_changed' | 'date_bounded';
      },
    ) =>
      client.patch<{ item?: Record<string, unknown>; success?: boolean }>(
        `/api/profiles/${profileId}/chat-memory/${id}`,
        body,
      ),

    deleteChatMemory: (profileId: string, id: string) =>
      client.delete<{ success: boolean }>(`/api/profiles/${profileId}/chat-memory/${id}`),

    create: (data: CreateProfileRequest) =>
      client.post<{ profile: Profile }>("/api/profiles", data),

    update: (id: string, data: Partial<CreateProfileRequest>) =>
      client.patch<{ profile: Profile }>(`/api/profiles/${id}`, data),

    backfill: (profileId: string, body?: { months?: number }) =>
      client.post<{ status: string; estimatedMinutes?: number }>(`/api/profiles/${profileId}/backfill`, body ?? {}),

    delete: (id: string) =>
      client.delete(`/api/profiles/${id}`),

    // Phase 3 M1 — Global Profile Selector
    accessible: () =>
      client.get<{ profiles: Array<Profile & { user_role: string }> }>('/api/profiles/accessible'),

    getActive: () =>
      client.get<{ profile: Profile & { user_role: string } }>('/api/profiles/active'),

    setActive: (profileId: string) =>
      client.post<{ ok: boolean; profile_id: string; role: string }>('/api/profiles/active', { profile_id: profileId }),

    // Week 33 — Profile lifecycle (archive, restore, transfer)
    limitStatus: () =>
      client.get<{ current: number; limit: number | null; percentage: number; planName: string; atLimit: boolean; nearLimit: boolean }>("/api/profiles/limit-status"),

    archive: (id: string) =>
      client.post<{ profile: Profile }>(`/api/profiles/${id}/archive`),

    restore: (id: string) =>
      client.post<{ profile: Profile }>(`/api/profiles/${id}/restore`),

    transferAgency: (id: string, agencyId: string) =>
      client.post<{ profile: Profile }>(`/api/profiles/${id}/transfer-agency`, { agencyId }),

    analytics: (id: string) =>
      client.get<{ profile_id: string; channel_count: number; last_activity_at: string | null }>(
        `/api/profiles/${id}/analytics`,
      ),

    // Pre-existing convenience methods
    campaigns: (profileId: string) =>
      client.get(`/api/profiles/${profileId}/campaigns`),

    sync: (profileId: string, syncType: string = 'full') =>
      client.post(`/api/profiles/${profileId}/sync`, { syncType }),

    getSyncStatus: (profileId: string) =>
      client.get(`/api/profiles/${profileId}/sync/status`),

    getSyncHistory: (profileId: string, limit: number = 10) =>
      client.get(`/api/profiles/${profileId}/sync/history`, { params: { limit } }),

    // Link accounts (Week 35 — Connect vs Sync)
    linkAccounts: (profileId: string, accountIds: string[]) =>
      client.post<{ linked: string[]; alreadyLinked: string[]; failed: any[] }>(`/api/profiles/${profileId}/link-accounts`, { accountIds }),
    unlinkAccount: (profileId: string, accountId: string) =>
      client.delete(`/api/profiles/${profileId}/link-accounts/${accountId}`),
    syncAccount: (profileId: string, accountId: string) =>
      client.post(`/api/profiles/${profileId}/link-accounts/${accountId}/sync`),
    syncAll: (profileId: string) =>
      client.post<{ jobsQueued: number; accounts: any[] }>(`/api/profiles/${profileId}/sync-all`),
    getLinkSyncStatus: (profileId: string) =>
      client.get<{ syncStatus: any[] }>(`/api/profiles/${profileId}/sync-status`),
    getLinkSyncHistory: (profileId: string, limit: number = 20) =>
      client.get<{ history: any[] }>(`/api/profiles/${profileId}/sync-history`, { params: { limit } }),

    // Team management
    getTeam: (profileId: string) =>
      client.get<{ team: Array<{ user_id: string; full_name: string; email: string; role: string; added_at: string }> }>(`/api/profiles/${profileId}/team`),

    addTeamMember: (profileId: string, email: string, role: string) =>
      client.post(`/api/profiles/${profileId}/team`, { email, role }),

    updateTeamMemberRole: (profileId: string, userId: string, role: string) =>
      client.put(`/api/profiles/${profileId}/team/${userId}`, { role }),

    removeTeamMember: (profileId: string, userId: string) =>
      client.delete(`/api/profiles/${profileId}/team/${userId}`),

    // Ownership
    getOwner: (profileId: string) =>
      client.get(`/api/profiles/${profileId}/owner`),

    transferOwnership: (profileId: string, newOwnerId: string) =>
      client.post(`/api/profiles/${profileId}/transfer`, { newOwnerId }),

    syncProgress: (profileId: string) =>
      client.get<{
        phase: "backfill" | "healing" | "complete" | "idle" | "failed" | string;
        percent: number;
        progress_pct?: number;
        stage: string | null;
        message: string;
        coverage_through: string | null;
        healing_tonight: boolean;
        data_ready: boolean;
        coverage_flags: {
          has_campaign_data: boolean;
          has_adgroup_data?: boolean;
          has_keyword_data?: boolean;
          has_search_term_data?: boolean;
          has_auction_data?: boolean;
          has_asset_data?: boolean;
          has_ga4_data?: boolean;
          has_shopify_data?: boolean;
          campaign_data_since: string | null;
          sync_health: string;
        };
        /** @deprecated alias of coverage_flags */
        coverage?: {
          has_campaign_data: boolean;
          campaign_data_since: string | null;
          sync_health: string;
        };
        jobs?: Array<Record<string, unknown>>;
      }>(`/api/profiles/${profileId}/sync-progress`),
  },

  // ── Channels (5 endpoints) ──────────────────────────────────────────────

  channels: {
    list: (profileId: string) =>
      client.get<{ channels: Channel[] }>(`/api/profiles/${profileId}/channels`),

    get: (profileId: string, id: string) =>
      client.get<{ channel: Channel }>(`/api/profiles/${profileId}/channels/${id}`),

    add: (profileId: string, data: CreateChannelRequest) =>
      client.post<{ channel: Channel }>(`/api/profiles/${profileId}/channels`, data),

    update: (profileId: string, id: string, data: Partial<CreateChannelRequest & { is_active: boolean }>) =>
      client.patch<{ channel: Channel }>(`/api/profiles/${profileId}/channels/${id}`, data),

    remove: (profileId: string, id: string) =>
      client.delete(`/api/profiles/${profileId}/channels/${id}`),
  },

  // ── Assignments (6 endpoints) ───────────────────────────────────────────

  assignments: {
    list: (profileId: string) =>
      client.get<{ assignments: any[] }>(`/api/profiles/${profileId}/assignments`),

    getUserAssignments: (profileId: string, userId: string) =>
      client.get(`/api/profiles/${profileId}/assignments/user/${userId}`),

    assignChannel: (profileId: string, data: AssignChannelRequest) =>
      client.post<{ assignment: ChannelAssignment }>(`/api/profiles/${profileId}/assignments/channel`, data),

    removeChannel: (profileId: string, id: string) =>
      client.delete(`/api/profiles/${profileId}/assignments/channel/${id}`),

    assignPM: (profileId: string, data: AssignPMRequest) =>
      client.post<{ assignment: PerformanceManager }>(`/api/profiles/${profileId}/assignments/performance-manager`, data),

    removePM: (profileId: string, id: string) =>
      client.delete(`/api/profiles/${profileId}/assignments/performance-manager/${id}`),
  },

  // ── Permissions (1 endpoint) ────────────────────────────────────────────

  permissions: {
    getUser: (profileId: string, userId: string) =>
      client.get<{ permissions: UserPermissions }>(`/api/profiles/${profileId}/permissions/user/${userId}`),
  },

  // ── Knowledge (5 endpoints) ─────────────────────────────────────────────

  knowledge: {
    list: (profileId: string, params?: KnowledgeListParams) =>
      client.get<{ items: KnowledgeItem[] }>(`/api/profiles/${profileId}/knowledge`, { params }),

    get: (profileId: string, id: string) =>
      client.get<{ item: KnowledgeItem }>(`/api/profiles/${profileId}/knowledge/${id}`),

    getRecent: (profileId: string) =>
      client.get<{ items: KnowledgeItem[] }>(`/api/profiles/${profileId}/knowledge/recent`),

    getSummary: (profileId: string, id: string) =>
      client.get(`/api/profiles/${profileId}/knowledge/${id}/summary`),

    upload: (profileId: string, data: CreateKnowledgeRequest) =>
      client.post<{ item: KnowledgeItem }>(`/api/profiles/${profileId}/knowledge`, data),

    uploadFile: (profileId: string, formData: FormData) =>
      client.post<{ item: KnowledgeItem }>(`/api/profiles/${profileId}/knowledge/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      }),

    inspectUploadSheets: (profileId: string, file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('knowledge_type', 'document');
      return client.post<{ sheetNames: string[] }>(
        `/api/profiles/${profileId}/knowledge/upload/inspect-sheets`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
    },

    // KC v2 — methods for the endpoints that already existed but had no
    // client bindings (the T2-3 dead-buttons root cause).
    update: (
      profileId: string,
      id: string,
      data: {
        title?: string;
        channel_id?: string | null;
        channel_tag?: string;
        document_type?: string;
        is_shared?: boolean;
        category?: string;
        content?: string;
        tags?: string[];
        description?: string;
        source?: string | null;
        expires_at?: string | null;
        intelligence_document_type?: string;
      },
    ) =>
      client.put<{ item: KnowledgeItem }>(`/api/profiles/${profileId}/knowledge/${id}`, data),

    summarize: (profileId: string, id: string) =>
      client.post<{
        item: KnowledgeItem;
        embedding_status?: string;
        embedding_stored?: boolean;
        embedding_reason?: string;
        embedding_error?: string;
      }>(`/api/profiles/${profileId}/knowledge/${id}/summarize`, {}),

    delete: (profileId: string, id: string) =>
      client.delete<{ success: boolean; id: string }>(`/api/profiles/${profileId}/knowledge/${id}`),

    bulk: (
      profileId: string,
      data: {
        operation: 'delete' | 'retag' | 'move_channel';
        item_ids: string[];
        channel_tag?: string;
        document_type?: string;
      },
    ) =>
      client.post<{
        success: boolean;
        operation: string;
        affected: number;
        partial_failures?: unknown;
      }>(`/api/profiles/${profileId}/knowledge/bulk`, data),

    download: (profileId: string, id: string) =>
      client.get<{ download_url: string }>(`/api/profiles/${profileId}/knowledge/${id}/download`),

    getAuditLog: (profileId: string, params?: { action?: string; limit?: number; offset?: number }) =>
      client.get<{ logs: any[] }>(`/api/profiles/${profileId}/knowledge/audit`, { params }),
  },

  // ── Knowledge-Scoped Comments (3 endpoints) ─────────────────────────────

  knowledgeComments: {
    list: (profileId: string, knowledgeId: string) =>
      client.get<{ comments: Comment[] }>(`/api/profiles/${profileId}/knowledge/${knowledgeId}/comments`),

    create: (profileId: string, knowledgeId: string, data: CreateCommentRequest) =>
      client.post<{ comment: Comment }>(`/api/profiles/${profileId}/knowledge/${knowledgeId}/comments`, data),

    count: (profileId: string, knowledgeId: string) =>
      client.get<{ total_comments: number; unresolved_count: number }>(`/api/profiles/${profileId}/knowledge/${knowledgeId}/comments/count`),
  },

  // ── Comment-Level Operations (6 endpoints) ──────────────────────────────

  comments: {
    get: (profileId: string, id: string) =>
      client.get<{ comment: Comment }>(`/api/profiles/${profileId}/comments/${id}`),

    update: (profileId: string, id: string, data: { comment_text: string }) =>
      client.patch<{ comment: Comment }>(`/api/profiles/${profileId}/comments/${id}`, data),

    delete: (profileId: string, id: string) =>
      client.delete(`/api/profiles/${profileId}/comments/${id}`),

    addReaction: (profileId: string, id: string, data: { reaction: string }) =>
      client.post<{ comment: Comment }>(`/api/profiles/${profileId}/comments/${id}/reactions`, data),

    resolve: (profileId: string, id: string, data: { is_resolved: boolean }) =>
      client.patch<{ comment: Comment }>(`/api/profiles/${profileId}/comments/${id}/resolve`, data),

    unresolved: (profileId: string) =>
      client.get<{ comments: Comment[] }>(`/api/profiles/${profileId}/comments/unresolved`),
  },

  // ── Channel Assignments (Week 34) ─────────────────────────────────────

  channelAssignments: {
    list: (profileId: string) =>
      client.get<{ assignments: any[] }>(`/api/profiles/${profileId}/channel-assignments`),

    assign: (profileId: string, targetUserId: string, channelId: string, notes?: string) =>
      client.post(`/api/profiles/${profileId}/channel-assignments`, { targetUserId, channelId, notes }),

    bulkAssign: (profileId: string, targetUserId: string, channelIds: string[]) =>
      client.post(`/api/profiles/${profileId}/channel-assignments/bulk`, { targetUserId, channelIds }),

    remove: (profileId: string, assignmentId: string) =>
      client.delete(`/api/profiles/${profileId}/channel-assignments/${assignmentId}`),

    myChannels: (profileId: string) =>
      client.get<{ channels: any[] }>(`/api/profiles/${profileId}/channel-assignments/my-channels`),
  },

  // ── Chat ────────────────────────────────────────────────────────────────

  chat: {
    send: (message: string, context?: { profileId?: string; dateRange?: { start: string; end: string } | null; channelId?: string | null; skillId?: string | null }) =>
      client.post("/api/chat/message", { message, context: context || undefined }),

    message: (payload: {
      message: string;
      profileId: string;
      sessionId?: string;
      channels?: string[];
      dateRange?: { start: string; end: string } | null;
      datePreset?: string | null;
      skillId?: string | null;
      selectedSkillKeys?: string[];
      /** Free-chat Intelligence — quick | deep | historic expert. */
      mode?: 'quick' | 'deep' | 'expert' | null;
      requestSchemaVersion?: number;
      clientRequestId?: string;
      depth?: string;
      isBriefRun?: boolean;
      briefDepth?: string | null;
      explicitRetry?: boolean;
      revisesTurnId?: string | null;
    }) => client.post("/api/chat/message", payload),

    /** Phase 4 — deployment lifecycle capability (pre-Send). */
    getChatCapabilities: () =>
      client.get<{ lifecycleControls: boolean }>("/api/chat/capabilities"),

    /** Phase 3B — request-claim status (no ChatAgent). */
    requestStatus: (clientRequestId: string, profileId: string) =>
      client.get(`/api/chat/requests/by-client-id/${clientRequestId}`, {
        params: { profileId },
      }),

    /**
     * Phase 4B — explicit Stop.
     * Body MUST be `{}`. Axios + application/json serializes `null` as the
     * invalid JSON document `"null"`, which Express strict JSON parsing rejects
     * before the cancel route (production 500).
     */
    cancelTurn: (turnId: string, profileId: string) =>
      client.post(`/api/chat/turns/${turnId}/cancel`, {}, {
        params: { profileId },
      }),

    /** Phase 4B — reconnect stream URL (caller uses fetch). */
    turnStreamPath: (turnId: string, profileId: string, after: number) =>
      `/api/chat/turns/${encodeURIComponent(turnId)}/stream?profileId=${encodeURIComponent(profileId)}&after=${encodeURIComponent(String(after))}`,

    /**
     * Active Recents history. Slice 6 uses pagination=cursor (opaque cursor + optional title q).
     * Legacy offset (page) remains available when pagination is omitted or 'offset'.
     */
    sessions: (opts: {
      profileId: string;
      pagination?: 'cursor' | 'offset';
      limit?: number;
      cursor?: string | null;
      q?: string;
      page?: number;
    }) => {
      const params: Record<string, string | number> = { profileId: opts.profileId };
      const mode = opts.pagination ?? (opts.page != null ? 'offset' : 'cursor');
      if (mode === 'cursor') {
        params.pagination = 'cursor';
        if (opts.limit != null) params.limit = opts.limit;
        if (opts.cursor) params.cursor = opts.cursor;
        if (opts.q) params.q = opts.q;
      } else {
        if (opts.page != null) params.page = opts.page;
        if (opts.limit != null) params.limit = opts.limit;
      }
      return client.get<{
        sessions: any[];
        limit?: number;
        hasMore?: boolean;
        nextCursor?: string | null;
        page?: number;
      }>('/api/chat/sessions', { params });
    },

    /** Archived Recents — same cursor/search contract as active list. */
    sessionsArchived: (opts: {
      profileId: string;
      pagination?: 'cursor';
      limit?: number;
      cursor?: string | null;
      q?: string;
    }) => {
      const params: Record<string, string | number> = {
        profileId: opts.profileId,
        pagination: 'cursor',
      };
      if (opts.limit != null) params.limit = opts.limit;
      if (opts.cursor) params.cursor = opts.cursor;
      if (opts.q) params.q = opts.q;
      return client.get<{
        sessions: any[];
        limit?: number;
        hasMore?: boolean;
        nextCursor?: string | null;
      }>('/api/chat/sessions/archived', { params });
    },

    archiveSession: (sessionId: string) =>
      client.post<{
        success: boolean;
        sessionId: string;
        archived: boolean;
        alreadyArchived: boolean;
        archivedAt: string | null;
      }>(`/api/chat/sessions/${sessionId}/archive`),

    restoreSession: (sessionId: string) =>
      client.post<{
        success: boolean;
        sessionId: string;
        archived: boolean;
        alreadyActive: boolean;
      }>(`/api/chat/sessions/${sessionId}/restore`),

    /**
     * Governed permanent delete. Caller must pass the user-typed confirmation phrase.
     * Backend requires exact confirm: "DELETE_PERMANENTLY".
     */
    deleteSession: (sessionId: string, confirm: string) =>
      client.delete(`/api/chat/sessions/${sessionId}`, {
        data: {
          confirm,
        },
      }),

    getSession: (sessionId: string) =>
      client.get(`/api/chat/sessions/${sessionId}`),

    updateSession: (sessionId: string, data: { title: string }) =>
      client.patch<{ success: boolean; title: string }>(`/api/chat/sessions/${sessionId}`, data),

    feedback: (messageId: string, helpful: boolean) =>
      client.post('/api/chat/feedback', { messageId, helpful }),
  },

  // ── Skills (prebuilt catalogue for Chat Skill selector) ───────────────

  skills: {
    prebuilt: () =>
      client.get("/api/skills/prebuilt"),
  },

  // ── Subscriptions (Week 26) ──────────────────────────────────────────

  subscriptions: {
    getPlans: () =>
      client.get("/api/subscriptions/plans"),

    getStatus: () =>
      client.get("/api/subscriptions/status"),

    create: (planName: string, billingCycle: 'monthly' | 'annual' = 'monthly') =>
      client.post("/api/subscriptions/create", { plan: planName, billing_cycle: billingCycle }),

    cancel: () =>
      client.post("/api/subscriptions/cancel"),
  },

  // ── Credits ───────────────────────────────────────────────────────────

  credits: {
    balance: () =>
      client.get("/api/credits/balance"),

    history: (limit?: number) =>
      client.get("/api/credits/history", { params: limit ? { limit } : undefined }),

    packages: () =>
      client.get("/api/stripe/packages"),

    // User-level credit management
    userBalance: () =>
      client.get("/api/credits/user-balance"),

    allocate: (data: { toUserId: string; amount: number; purpose?: string; profileId?: string }) =>
      client.post("/api/credits/allocate", data),

    reclaim: (data: { fromUserId: string; amount: number; purpose?: string }) =>
      client.post("/api/credits/reclaim", data),

    allocationHistory: (limit?: number) =>
      client.get("/api/credits/allocation-history", { params: limit ? { limit } : undefined }),

    companyBalances: () =>
      client.get("/api/credits/company-balances"),
  },

  // ── Notifications ─────────────────────────────────────────────────────

  notifications: {
    list: (params?: { unread_only?: boolean; limit?: number }) =>
      client.get<{ count: number; notifications: Array<{
        id: string;
        company_id: string;
        type: string;
        title: string;
        message: string;
        read: boolean;
        created_at: string;
      }> }>("/api/notifications", { params }),

    markRead: (id: string) =>
      client.post(`/api/notifications/${id}/read`),
  },

  // ── Auto-Recharge ──────────────────────────────────────────────────────

  autoRecharge: {
    getSettings: () =>
      client.get("/api/auto-recharge/settings"),

    updateSettings: (data: { threshold?: number; rechargeAmount?: number; maxMonthly?: number }) =>
      client.put("/api/auto-recharge/settings", data),

    enable: (data: { threshold: number; rechargeAmount: number; maxMonthly?: number }) =>
      client.post("/api/auto-recharge/enable", data),

    disable: () =>
      client.post("/api/auto-recharge/disable"),
  },

  // ── Me / unified scope (KB1 — P-01) ─────────────────────────────────

  me: {
    getScope: (profileId?: string) =>
      client.get('/api/me/scope', {
        params: profileId ? { profileId } : undefined,
      }),
  },

  // ── Roles & Permissions (Week 28) ──────────────────────────────────

  roles: {
    getAllRoles: () =>
      client.get("/api/roles"),

    getMyPermissions: () =>
      client.get("/api/roles/my-permissions"),

    getMyRoles: () =>
      client.get("/api/roles/my-roles"),

    getMyChannels: (profileId?: string) =>
      client.get("/api/roles/my-channels", { params: profileId ? { profile_id: profileId } : undefined }),

    getCompanyAssignments: () =>
      client.get("/api/roles/company-assignments"),

    assignRole: (userId: string, roleName: string, expiresAt?: string) =>
      client.post("/api/roles/assign", { user_id: userId, role_name: roleName, expires_at: expiresAt }),

    revokeRole: (userId: string, roleName: string) =>
      client.post("/api/roles/revoke", { user_id: userId, role_name: roleName }),

    getUserPermissions: (userId: string) =>
      client.get(`/api/roles/user/${userId}/permissions`),

    getUserRoles: (userId: string) =>
      client.get(`/api/roles/user/${userId}/roles`),

    getAuditLog: (limit?: number) =>
      client.get("/api/roles/audit-log", { params: limit ? { limit } : undefined }),

    getCompanyAuditLog: (limit?: number) =>
      client.get("/api/roles/company-audit-log", { params: limit ? { limit } : undefined }),

    bulkAssignRoles: (userIds: string[], roleName: string) =>
      client.post("/api/roles/bulk-assign", { user_ids: userIds, role_name: roleName }),

    assignChannel: (userId: string, profileId: string, channelId: string) =>
      client.post("/api/roles/channel-assign", { user_id: userId, profile_id: profileId, channel_id: channelId }),

    revokeChannel: (userId: string, profileId: string, channelId: string) =>
      client.post("/api/roles/channel-revoke", { user_id: userId, profile_id: profileId, channel_id: channelId }),

    grantProfileAccess: (userId: string, profileId: string, role?: string) =>
      client.post("/api/roles/profile-access", { user_id: userId, profile_id: profileId, role }),

    revokeProfileAccess: (userId: string, profileId: string) =>
      client.delete("/api/roles/profile-access", { data: { user_id: userId, profile_id: profileId } }),
  },

  // ── Invitations (Week 29) ────────────────────────────────────────────

  invitations: {
    sendInvitation: (email: string, roleId: string, initialCredits?: number, message?: string) =>
      client.post("/api/invitations/send", {
        email,
        role_id: roleId,
        initial_credits: initialCredits,
        invitation_message: message,
      }),

    sendByRoleName: (data: {
      email: string;
      role_name: string;
      company_id?: string;
    }) => client.post<{ success: boolean; email: string }>("/api/invitations/send-by-role-name", data),

    getInvitationByToken: (token: string) =>
      client.get(`/api/invitations/${token}`),

    acceptInvitation: (token: string) =>
      client.post("/api/invitations/accept", { token }),

    declineInvitation: (token: string) =>
      client.post("/api/invitations/decline", { token }),

    revokeInvitation: (invitationId: string, reason?: string) =>
      client.post("/api/invitations/revoke", { invitation_id: invitationId, reason }),

    resendInvitation: (invitationId: string) =>
      client.post("/api/invitations/resend", { invitation_id: invitationId }),

    getCompanyInvitations: (status?: string) =>
      client.get("/api/invitations/company", { params: status ? { status } : undefined }),

    deleteInvitation: (invitationId: string) =>
      client.delete(`/api/invitations/${invitationId}`),
  },

  // ── Platform Admin (Week 29 Day 6) ─────────────────────────────────

  platform: {
    getMyPlatformPermissions: () =>
      client.get("/api/platform/my-permissions"),

    getAllCompanies: () =>
      client.get("/api/platform/companies"),

    assignPlatformRole: (userId: string, roleName: string, notes?: string) =>
      client.post("/api/platform/assign-role", { user_id: userId, role_name: roleName, notes }),

    revokePlatformRole: (userId: string, roleName: string) =>
      client.post("/api/platform/revoke-role", { user_id: userId, role_name: roleName }),

    startSupportSession: (companyId: string, reason: string, durationHours?: number) =>
      client.post("/api/platform/support-session/start", {
        company_id: companyId,
        reason,
        duration_hours: durationHours,
      }),

    endSupportSession: (sessionId: string, reason?: string) =>
      client.post("/api/platform/support-session/end", { session_id: sessionId, reason }),

    logSupportAction: (sessionId: string, action: string, details?: any) =>
      client.post("/api/platform/support-session/log", { session_id: sessionId, action, details }),
  },

  // ── Stripe Checkout ─────────────────────────────────────────────────

  stripe: {
    createCheckoutSession: (packageName: string) =>
      client.post("/api/stripe/checkout", { package: packageName }),
  },

  // ── Google Ads ──────────────────────────────────────────────────────────

  googleAds: {
    accounts: () => client.get<{ accounts: Array<Record<string, unknown>> }>("/api/google-ads/accounts"),
    refreshAccounts: () => client.post("/api/google-ads/accounts/refresh"),
    accessibleAccounts: () =>
      client.get<{
        tree: Array<{
          id: string;
          name: string;
          level: number;
          isManager: boolean;
          currency: string | null;
          status: string | null;
          children: Array<{
            id: string;
            name: string;
            level: number;
            isManager: boolean;
            currency: string | null;
            status: string | null;
            children: unknown[];
          }>;
        }>;
        accounts: Array<{
          customerId: string;
          name: string;
          isManager: boolean;
          mccId: string | null;
          parentManagerIds?: string[];
          currency?: string | null;
          status?: string | null;
        }>;
      }>("/api/google-ads/accessible-accounts"),
    selectAccount: (data: {
      profileId: string;
      customerId: string;
      accountName: string;
      candidateManagerIds?: string[];
    }) => client.post("/api/google-ads/select-account", data),
    /** Wave 11.3 B3 — re-prove stored pairing after OAuth reconnect (Ruling R-A) */
    reprovePairing: (data: { profileId: string }) =>
      client.post<
        | { ok: true; needsPicker?: false; accountName?: string }
        | { ok: false; reason?: string; needsPicker: true }
      >("/api/google-ads/reprove-pairing", data),
  },

  ga4: {
    status: (profileId?: string) =>
      client.get<{
        connected: boolean;
        needs_reconnect?: boolean;
        token_expires_at?: string | null;
      }>("/api/oauth/ga4/status", {
        params: profileId ? { profileId } : {},
      }),
    properties: () =>
      client.get<{
        properties: Array<{
          name: string;
          displayName: string;
          propertyId: string;
          websiteUrl: string;
          timeZone: string;
          currencyCode: string;
        }>;
      }>("/api/oauth/ga4/properties"),
    selectProperty: (data: {
      profileId: string;
      propertyId: string;
      displayName: string;
      websiteUrl?: string;
    }) => client.post("/api/oauth/ga4/select-property", data),
    disconnect: (profileId?: string) =>
      client.delete("/api/oauth/ga4/disconnect", { data: { profileId } }),
  },

  metaAds: {
    accounts: () => client.get<{ accounts: Array<Record<string, unknown>> }>("/api/meta/accounts"),
  },

  linkedinAds: {
    accounts: () => client.get<{ accounts: Array<Record<string, unknown>> }>("/api/linkedin/accounts"),
  },

  // ── OAuth ───────────────────────────────────────────────────────────────

  oauth: {
    google: {
      authorize: (params?: { profileId?: string; oauthReturn?: "integrations" }) =>
        client.get("/api/oauth/google-ads/authorize", { params }),
      status: (profileId?: string) =>
        client.get<{
          connected: boolean;
          needs_reconnect?: boolean;
          reason?: string | null;
          reconnect_reason?: string | null;
        }>("/api/oauth/google-ads/status", {
          params: profileId ? { profileId } : {},
        }),
    },
    meta: {
      authorize: (params?: { profileId?: string; oauthReturn?: "integrations" }) =>
        client.get("/api/oauth/meta/authorize", { params }),
      status: (profileId?: string) =>
        client.get<{
          connected: boolean;
          needs_reconnect?: boolean;
          reason?: string | null;
          reconnect_reason?: string | null;
        }>("/api/oauth/meta/status", {
          params: profileId ? { profileId } : {},
        }),
    },
    linkedin: {
      authorize: (params?: { profileId?: string; oauthReturn?: "integrations" }) =>
        client.get("/api/oauth/linkedin/authorize", { params }),
      status: (profileId?: string) =>
        client.get<{
          connected: boolean;
          needs_reconnect?: boolean;
          reason?: string | null;
          reconnect_reason?: string | null;
        }>("/api/oauth/linkedin/status", {
          params: profileId ? { profileId } : {},
        }),
    },
  },

  // ── Admin Dashboard (Week 37) ───────────────────────────────────────────

  admin: {
    getDashboard: () =>
      client.get("/api/admin/dashboard"),
    getTurnTrace: (turnId: string) =>
      client.get(`/api/admin/turn-trace/${turnId}`),
  },

  // ── Agencies (Week 37) ──────────────────────────────────────────────────

  agencies: {
    create: (data: { name: string; website?: string; companySize?: string; clientCountRange?: string; industryFocus?: string }) =>
      client.post("/api/agencies", data),

    get: (id: string) =>
      client.get(`/api/agencies/${id}`),

    update: (id: string, data: { name?: string; website?: string }) =>
      client.put(`/api/agencies/${id}`, data),

    getTeam: (id: string) =>
      client.get(`/api/agencies/${id}/team`),

    getProfiles: (id: string) =>
      client.get(`/api/agencies/${id}/profiles`),

    getCreditPool: (id: string) =>
      client.get(`/api/agencies/${id}/credit-pool`),

    invitePM: (id: string, data: { email: string; monthlyLimit?: number | null; alertThreshold?: number }) =>
      client.post(`/api/agencies/${id}/invite-pm`, data),

    removeMember: (id: string, userId: string) =>
      client.delete(`/api/agencies/${id}/team/${userId}`),
  },

  // ── Companies ─────────────────────────────────────────────────────────

  companies: {
    getPreferences: (id: string) =>
      client.get(`/api/companies/${id}/preferences`),

    updatePreferences: (id: string, data: any) =>
      client.put(`/api/companies/${id}/preferences`, data),
  },

  // ── Onboarding (Week 36) ────────────────────────────────────────────────

  onboarding: {
    getStatus: () =>
      client.get<any>('/api/onboarding/status'),
    getFlow: () =>
      client.get<{ flow_type: string; steps: string[]; step_labels: Record<string, string>; total_steps: number }>('/api/onboarding/flow'),
    completeStep: (step: string, stepData?: Record<string, unknown>) =>
      client.post<any>('/api/onboarding/complete-step', { step, stepData }),
    skipStep: (stepName: string) =>
      client.post<any>('/api/onboarding/skip-step', { stepName }),
    detectFlow: (flowType?: string, invitationToken?: string) =>
      client.post<any>('/api/onboarding/detect-flow', { flowType, invitationToken }),
    skip: () =>
      client.post<any>('/api/onboarding/skip'),
  },

  customRequests: {
    create: (body: {
      profileCount: string;
      requirements: string[];
      contactName?: string;
      contactDetail?: string;
      company_id?: string | null;
    }) => client.post<{ success: boolean }>('/api/custom-requests', body),
  },

  // ── Anomaly Detector (GET /api/anomalies/:profileId) ───────────────────────

  anomalies: {
    list: (
      profileId: string,
      params?: { status?: string; severity?: string; limit?: number },
    ) =>
      client.get<{
        alerts: Array<Record<string, unknown>>;
        total: number;
      }>(`/api/anomalies/${profileId}`, { params }),

    summary: (profileId: string) =>
      client.get<{
        total: number;
        by_severity: Record<string, number>;
        by_status: Record<string, number>;
      }>(`/api/anomalies/${profileId}/summary`),

    acknowledge: (alertId: string, profileId: string) =>
      client.patch<{ id: string; status: string; triggered_at: string }>(
        `/api/anomalies/${alertId}/acknowledge`,
        { profile_id: profileId },
      ),
  },

  // ── Insights — Recommendations (GET /api/recommendations/:profileId) ───────

  recommendations: {
    list: (
      profileId: string,
      params?: { type?: string; campaign_id?: string; platform?: string; verdict?: string; limit?: number },
    ) =>
      client.get<{
        recommendations: Array<Record<string, unknown>>;
        total: number;
      }>(`/api/recommendations/${profileId}`, { params }),

    summary: (profileId: string) =>
      client.get<{
        total: number;
        by_recommendation_type: Record<string, number>;
        by_verdict: Record<string, number>;
        account_optimization_score: number | null;
      }>(`/api/recommendations/${profileId}/summary`),

    review: (profileId: string, recommendationId: string, action: 'accepted' | 'declined' | 'dismissed') =>
      client.patch<Record<string, unknown>>(
        `/api/recommendations/${profileId}/${recommendationId}/review`,
        { action },
      ),
  },

  // ── Insights — Change Ledger (GET /api/changes/:profileId) ───────────────

  changes: {
    list: (
      profileId: string,
      params?: {
        days?: number;
        platform?: string;
        change_type?: string;
        entity_type?: string;
        limit?: number;
        offset?: number;
      },
    ) =>
      client.get<{
        changes: Array<Record<string, unknown>>;
        total: number;
        limit: number;
        offset: number;
        days_covered: number;
      }>(`/api/changes/${profileId}`, { params }),

    summary: (
      profileId: string,
      params?: { days?: number; platform?: string },
    ) =>
      client.get<{
        total: number;
        by_change_type: Record<string, number>;
        days_covered: number;
        fetch_coverage: Record<string, unknown>;
      }>(`/api/changes/${profileId}/summary`, { params }),
  },

  // ── Concierge — big-fetch confirm (Wave 4) ───────────────────────────────

  concierge: {
    fetchConfirm: (
      profileId: string,
      body: {
        action?: 'confirm' | 'dismiss';
        window_start?: string;
        window_end?: string;
        stage?: string;
        scope?: 'totals' | 'campaign' | 'breakdown';
        stages?: string[];
        outside_retention?: boolean;
        message_id?: string;
        block_index?: number;
      },
    ) =>
      client.post<{
        ok: boolean;
        outcome?: 'confirmed' | 'dismissed' | 'joined';
        job_id?: string;
        eta_minutes?: number;
        queued_line?: string;
      }>(`/api/concierge/${profileId}/fetch-confirm`, body),
  },

  // ── Targets — chat write-back confirm (DEC-M7) ───────────────────────────

  targets: {
    chatConfirmLookup: (
      profileId: string,
      params: {
        metric_key: string;
        channel?: string | null;
        period_type?: string;
        stated_segment?: string;
        session_channels?: string;
      },
    ) =>
      client.get<{
        existing: {
          id: string;
          target_value: number;
          version: number;
        } | null;
      }>(`/api/targets/${profileId}/chat-confirm/lookup`, { params }),

    chatConfirm: (
      profileId: string,
      body: {
        action?: string;
        metric_key?: string;
        target_value?: number;
        channel?: string | null;
        period_type?: string;
        entry_type?: string;
        human_summary?: string;
        message_id?: string;
        block_index?: number;
        id?: string;
        expected_version?: number;
        session_channels?: string[];
        stated_segment?: string | null;
      },
    ) =>
      client.post<{
        success: boolean;
        outcome?: 'confirmed' | 'dismissed';
        target_id?: string;
        outcome_persisted?: boolean;
        noop?: boolean;
      }>(`/api/targets/${profileId}/chat-confirm`, body),
  },

  // ── Chat prompts (prebuilt library) ─────────────────────────────────────

  prompts: {
    list: (profileId?: string | null) =>
      client.get<{
        prompts: Array<{
          id: string;
          title: string;
          prompt_text: string;
          category: string;
          depth_tag: 'ask' | 'analyse' | 'audit' | 'report' | null;
          channels: string[] | null;
          scope: 'single_channel' | 'cross_channel' | 'any';
          business_types: string[] | null;
          requires_data: string[] | null;
          is_prebuilt: boolean;
          profile_id: string | null;
          sort_order: number;
          is_active: boolean;
          use_count: number;
          created_at: string;
        }>;
      }>('/api/prompts', {
        params: profileId ? { profileId } : undefined,
      }),

    create: (payload: {
      profile_id: string;
      title: string;
      prompt_text: string;
      category: string;
      depth_tag?: string | null;
    }) =>
      client.post<{ prompt: Record<string, unknown> }>('/api/prompts', payload),

    use: (id: string) => client.post<void>(`/api/prompts/${id}/use`),
  },
};

export default api;
